import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, User, Calendar, X, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BedInfo {
  id: number;
  bedName: string;
  roomName: string | null;
  status: string;
}

interface ClientResult {
  id: number;
  firstName: string | null;
  lastName: string | null;
  internalId: string;
  intakeDate: string | null;
  status: string;
}

interface SearchResponse {
  data: ClientResult[];
  meta: { total: number };
}

interface AssignResponse {
  data: Record<string, unknown>;
  message: string;
}

interface BedBoardEntry {
  bed: { id: number; bedName: string; roomName: string | null };
  occupant: { personId: number } | null;
  status: string;
}

interface BedBoardResponse {
  data: {
    beds: BedBoardEntry[];
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface BedAssignDialogProps {
  bed: BedInfo;
  onClose: () => void;
  onAssigned: () => void;
}

export function BedAssignDialog({
  bed,
  onClose,
  onAssigned,
}: BedAssignDialogProps) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState<ClientResult | null>(
    null
  );
  const [assignDate, setAssignDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [notes, setNotes] = useState("");

  // Fetch current bed board to check for existing assignments
  const { data: boardData } = useQuery<BedBoardResponse>({
    queryKey: ["bed-board"],
    queryFn: () => api.get<BedBoardResponse>("/shelter/board"),
  });

  // Build a map of personId -> bed info for "already assigned" warnings
  const occupantBedMap = new Map<number, { bedName: string; roomName: string | null }>();
  if (boardData?.data?.beds) {
    for (const entry of boardData.data.beds) {
      if (entry.occupant && entry.status === "occupied") {
        occupantBedMap.set(entry.occupant.personId, {
          bedName: entry.bed.bedName,
          roomName: entry.bed.roomName,
        });
      }
    }
  }

  // Search clients
  const { data: searchResults, isLoading: isSearching } =
    useQuery<SearchResponse>({
      queryKey: ["client-search", searchQuery],
      queryFn: () =>
        api.get<SearchResponse>("/clients", {
          params: {
            query: searchQuery,
            pageSize: "10",
          },
        }),
      enabled: searchQuery.length >= 2,
    });

  // Assign mutation
  const assignMutation = useMutation({
    mutationFn: () =>
      api.post<AssignResponse>(`/shelter/beds/${bed.id}/assign`, {
        personId: String(selectedClient!.id),
        date: assignDate,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bed-board"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-beds"] });
      onAssigned();
    },
  });

  const clients = searchResults?.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-lg rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <div>
            <h2 className="text-lg font-semibold">Assign Bed</h2>
            <p className="text-sm text-muted-foreground">
              {bed.roomName ? `${bed.roomName} - ` : ""}
              {bed.bedName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-accent"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          {/* Bed status */}
          <div className="flex items-center gap-2 rounded-md bg-muted p-3">
            <div
              className={cn(
                "h-3 w-3 rounded-full",
                bed.status === "available"
                  ? "bg-green-500"
                  : bed.status === "occupied"
                    ? "bg-blue-500"
                    : "bg-gray-500"
              )}
            />
            <span className="text-sm font-medium capitalize">
              {bed.status}
            </span>
          </div>

          {/* Client search */}
          {!selectedClient ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">Search Client</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by name or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  autoFocus
                />
              </div>

              {/* Search results */}
              {isSearching && (
                <p className="py-2 text-center text-sm text-muted-foreground">
                  Searching...
                </p>
              )}

              {searchQuery.length >= 2 && !isSearching && clients.length === 0 && (
                <p className="py-2 text-center text-sm text-muted-foreground">
                  No clients found.
                </p>
              )}

              {clients.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-md border">
                  {clients.map((client) => {
                    const existingBed = occupantBedMap.get(client.id);
                    return (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => {
                          if (existingBed) return; // Don't allow selecting if already assigned
                          setSelectedClient(client);
                        }}
                        disabled={!!existingBed}
                        className={cn(
                          "flex w-full items-center gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0",
                          existingBed
                            ? "cursor-not-allowed bg-yellow-50 opacity-80"
                            : "hover:bg-accent"
                        )}
                      >
                        <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">
                            {client.firstName} {client.lastName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            ID: {client.internalId}
                            {client.intakeDate &&
                              ` | Intake: ${new Date(client.intakeDate).toLocaleDateString()}`}
                          </p>
                          {existingBed && (
                            <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-amber-600">
                              <AlertTriangle className="h-3 w-3" />
                              Already in bed{" "}
                              {existingBed.roomName
                                ? `${existingBed.roomName} - `
                                : ""}
                              {existingBed.bedName}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Selected Client</label>
              <div className="flex items-center justify-between rounded-md border bg-muted/50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {selectedClient.firstName} {selectedClient.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ID: {selectedClient.internalId}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedClient(null)}
                  className="rounded-md p-1 hover:bg-accent"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Assignment date */}
          <Input
            label="Assignment Date"
            type="date"
            value={assignDate}
            onChange={(e) => setAssignDate(e.target.value)}
            required
          />

          {/* Notes */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {/* Error display */}
          {assignMutation.isError && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {(assignMutation.error as any)?.body?.error ??
                  (assignMutation.error as Error)?.message ??
                  "Failed to assign bed"}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t p-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => assignMutation.mutate()}
            disabled={!selectedClient || !assignDate}
            loading={assignMutation.isPending}
          >
            <Calendar className="mr-1 h-4 w-4" />
            Assign Bed
          </Button>
        </div>
      </div>
    </div>
  );
}
